import React, {useEffect} from "react";
import {Link} from "react-router-dom";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import {useTheme} from "@mui/material/styles";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import {Checkbox, CircularProgress} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Alert from "@mui/material/Alert";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import AlertTitle from "@mui/material/AlertTitle";
import DialogActions from "@mui/material/DialogActions";
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import {useTranslation} from "react-i18next";
import {UserContext} from "../App";
import {deleteData, fetchData, fetchDataBlob} from "../utils/api";
import AnalyzeClassification from "./AnalyzeClassification";
import AnalyzeRegression from "./AnalyzeRegression";
import AnalyzeForecast from "./AnalyzeForecast";
import AnalyzeAFS from "./AnalyzeAFS";


export default function Analyzer() {
    const theme = useTheme();
    // init internationalization
    const {t} = useTranslation();

    // get data from current dataset + Experiment
    const {
        datasets,
        currentDatasetIdx,
        experiments,
        currentExperimentIdx,
        currentExperimentProgress,
    } = React.useContext(UserContext);

    const [selectedParentRun, setSelectedParentRun] = React.useState("");
    const [selectedRun, setSelectedRun] = React.useState("");
    const [runsFamilyData, setRunsFamilyData] = React.useState([]);
    const [childRunsNames, setChildRunsNames] = React.useState([]);
    const [runData, setRunData] = React.useState({
        metrics: {},
        predictionDetails: {classNames: []},
        params: {stepName: ""},
    });
    const [open, setOpen] = React.useState(false);

    const fetchRun = (runName) => {
        fetchData(
            `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experiments[currentExperimentIdx].experimentName}/run/${runName}`
        ).then((data) => {
            setRunData(data);

        });
    };
    const fetchRuns = () => {
        fetchData(
            `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experiments[currentExperimentIdx].experimentName}/runs`
        ).then((data) => {
            setRunsFamilyData(data.runs);
            // if there is only one run, set it as selected
            if (data.runs.length === 1) {
                setSelectedParentRun(data.runs.at(0).parent.runName);
                setChildRunsNames(data.runs.at(0).children.map((child) => child.runName));
                // set last child run as selected
                const lastChildRun = data.runs.at(0).children.at(data.runs.at(0).children.length - 1);
                fetchRun(lastChildRun.runName);
                setSelectedRun(lastChildRun.runName);

            }
        });
    };
    const [downloadModelClicked, setDownloadModelClicked] = React.useState(false);
    const [openDownload, setOpenDownload] = React.useState(false);
    const [includeValidation, setIncludeValidation] = React.useState(false);
    const [includeTrain, setIncludeTrain] = React.useState(false);
    const [includeLinuxPythonEnv, setIncludeLinuxPythonEnv] = React.useState(false);
    const [includeWindowsPythonEnv, setIncludeWindowsPythonEnv] = React.useState(false);
    const handleOpenDownloadSelecter = () => setOpenDownload(true);
    const handleCloseDownloadSelecter = () => setOpenDownload(false);
    const downloadModel = (runName) => {
        // Only include parameters if they are `true`
        const params = new URLSearchParams();
        const lan = window.localStorage.getItem("languageSet")
        if (includeValidation) params.append("includeValidation", "true");
        if (includeTrain) params.append("includeTrain", "true");
        if (includeLinuxPythonEnv) params.append("includeLinuxDownload", "true");
        if (includeWindowsPythonEnv) params.append("includeWindowsDownload", "true");
        params.append("language", lan);


        const url = `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${encodeURIComponent(datasets[currentDatasetIdx].datasetName)}/experiment/${encodeURIComponent(experiments[currentExperimentIdx].experimentName)}/download-model/${encodeURIComponent(runName)}?${params.toString()}`;

        fetchDataBlob(url)
            .then((downloadUrl) => {
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', `${runName}.zip`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            handleCloseDownloadSelecter();
            setDownloadModelClicked(false);
        });
    };
    const handleDownload = () => {
        downloadModel(selectedRun, { includeValidation, includeTrain });
    };
    // delete selected Run
    const handleRunDelete = async () => {
        try {
            await deleteData(
                `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experiments[currentExperimentIdx].experimentName}/run/${selectedRun}`
            ).then(async (data) => {
                if (data.status === 200) {
                    // close dialog
                    setOpen(false);
                    // reset run data
                    setRunData({
                        metrics: {},
                        predictionDetails: {classNames: []},
                        params: {},
                    })
                    fetchRuns();

                }
            });
        } catch (e) {
            console.log(e);
        }
    };
    // fetch runs data by API requests, when the current experiment is gathered
    useEffect(() => {
        if (datasets.length >= 1) {
            fetchRuns();
        }
    }, [experiments, currentExperimentIdx]);

    const handleParentRunSelectorChange = (event) => {
        const runFamilyData = runsFamilyData.find((run) => run.parent.runName === event.target.value);
        setSelectedParentRun(event.target.value);
        setChildRunsNames(runFamilyData.children.map((child) => child.runName));
        // set last child run as selected
        const lastChildRun = runFamilyData.children.at(runFamilyData.children.length - 1);
        fetchRun(lastChildRun.runName);
        setSelectedRun(lastChildRun.runName);
    };

    const handleSelectorChange = (event) => {
        setSelectedRun(event.target.value);
        fetchRun(event.target.value);
    };

    // Functions for the Dialog
    const handleDialogDisplay = () => {
        setOpen(!open);
    };


    return (
        <Grid
            id="analyzer-container"
            container
        >
            <Grid
                container
                sx={{
                    padding: theme.spacing(8, 8),
                    borderRadius: '16px',
                    backgroundColor: 'white',
                }}
            >
                {/* Dialog to check if the selection shall be deleted */}
                <Dialog
                    open={open}
                    onClose={handleDialogDisplay}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogContent>
                        <Alert severity="warning" className="abortUploadWarning"
                               icon={<WarningAmberIcon
                                   sx={{
                                       alignItems: "center",
                                       width: "48px",
                                       height: "48px",
                                       color: theme.palette.error.main
                                   }}/>}>
                            <AlertTitle><Typography variant="h4"
                                                    color="text.secondary">{t("common.Warning")}</Typography>
                            </AlertTitle>
                            <Typography variant="h5"
                                        color="text.tertiary">{t("overview.DeleteRunWarning")}</Typography>
                        </Alert>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="contained" color="primary" component="span"
                                size="medium"
                                onClick={handleRunDelete}
                                autoFocus>
                            {t("common.Agree")}
                        </Button>
                        <Button variant="contained" color="primary" component="span"
                                size="medium" onClick={handleDialogDisplay}> {t("common.Disagree")}</Button>

                    </DialogActions>
                </Dialog>
                <Grid item xs={12}>
                    <Typography variant="h4" gutterBottom>{t("analyzer.PageTitle")}</Typography>
                </Grid>
                {
                    // only render if current experiment has fulfilled all required steps and stats are loaded
                    currentExperimentProgress?.trained > 0 ? (
                        <>
                            <Grid item lg={3} xs={6} sx={{margin: theme.spacing(3, 0)}}>
                                <FormControl>
                                    <InputLabel>{t("analyzer.SelectedRunDropdown")}</InputLabel>
                                    <Select
                                        value={selectedParentRun}
                                        label={t("analyzer.SelectedRunDropdown")}
                                        onChange={handleParentRunSelectorChange}
                                        sx={{minWidth: 200}}
                                    >
                                        {runsFamilyData.map((data, index) => (
                                            <MenuItem key={`run-selection-${index}`} value={data.parent.runName}>
                                                {data.parent.runName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>
                                        {t("analyzer.DescriptionSelectRun")}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid item lg={3} xs={6} sx={{margin: theme.spacing(3, 0)}}>
                                {childRunsNames.length > 0 && (
                                    <FormControl>
                                        <InputLabel>{t("analyzer.SelectedPipelineStepDropdown")}</InputLabel>
                                        <Select
                                            value={selectedRun}
                                            label={t("analyzer.SelectedPipelineStepDropdown")}
                                            onChange={handleSelectorChange}
                                            sx={{minWidth: 200}}
                                        >
                                            {childRunsNames.map((name, index) => (
                                                <MenuItem key={`run-selection-${index}`} value={name}>
                                                    {name.replace(`${selectedParentRun}_`, '')}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Grid>
                            {
                                runData && selectedRun && (
                                    <Grid item lg={6} xs={12} sx={{margin: theme.spacing(3, 0)}}>
                                        <Grid container spacing={2} alignItems="flex-start">
                                            <Grid item xs={12}>
                                                <Link
                                                    to={`/train/${datasets[currentDatasetIdx].datasetName}/${experiments[currentExperimentIdx].experimentName}/pipeline/${selectedParentRun}`}
                                                    style={{textDecoration: 'none'}}
                                                >
                                                    <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        size="medium"
                                                        startIcon={<QueryStatsIcon/>}
                                                    >
                                                        {t("analyzer.GoToPipelineButton")}
                                                    </Button>
                                                </Link>
                                            </Grid>
                                            <Grid item lg={4} xs={6}>
                                                <Button
                                                    onClick={handleOpenDownloadSelecter}
                                                    variant="outlined"
                                                    color="primary"
                                                    disabled={runData.params.stepName.startsWith("AFS")}
                                                    startIcon={<FileDownloadIcon/>}
                                                >
                                                    {t("analyzer.DownloadModelButton")}
                                                </Button>

                                                <Dialog open={openDownload} onClose={handleCloseDownloadSelecter}>
                                                    <DialogTitle variant="h4">{t("analyzer.DownloadOptions")}</DialogTitle>
                                                    <DialogContent>
                                                        <FormControlLabel
                                                            control={<Checkbox checked={includeValidation} onChange={() => setIncludeValidation(!includeValidation)} />}
                                                            label={t("analyzer.IncludeValidation")}
                                                        />
                                                        <FormControlLabel
                                                            control={<Checkbox checked={includeTrain} onChange={() => setIncludeTrain(!includeTrain)} />}
                                                            label={t("analyzer.IncludeTrainData")}
                                                        />
                                                        <Typography variant="h5">
                                                            {t("analyzer.Warning")}
                                                        </Typography>
                                                        <FormControlLabel
                                                            control={<Checkbox checked={includeLinuxPythonEnv} onChange={() => setIncludeLinuxPythonEnv(!includeLinuxPythonEnv)} />}
                                                            label={t("analyzer.IncludeLinux")}
                                                        />
                                                        <FormControlLabel
                                                            control={<Checkbox checked={includeWindowsPythonEnv} onChange={() => setIncludeWindowsPythonEnv(!includeWindowsPythonEnv)} />}
                                                            label={t("analyzer.IncludeWindows")}
                                                        />
                                                    </DialogContent>
                                                    <DialogActions>
                                                        <Button onClick={handleCloseDownloadSelecter} color="secondary">
                                                            {!downloadModelClicked ? t("analyzer.Cancel") : t("analyzer.CloseWindow")}
                                                        </Button>
                                                        {!downloadModelClicked ? (
                                                            <Button onClick={() => {
                                                                setDownloadModelClicked(true);
                                                                handleDownload();
                                                            }} color="primary" variant="contained" startIcon={<FileDownloadIcon />}>
                                                                {t("analyzer.DownloadModelButton")}
                                                            </Button>
                                                        ) : (
                                                            <div>
                                                                <CircularProgress/>
                                                                <Typography variant="body1" component="p">
                                                                &nbsp;{t("analyzer.CreatingDownloadZip")}
                                                                </Typography>
                                                            </div>
                                                        )}
                                                    </DialogActions>
                                                </Dialog>
                                            </Grid>
                                            <Grid item lg={4} xs={6}>
                                                {/* Delete Button */}
                                                <Button
                                                    onClick={handleDialogDisplay}
                                                    variant="outlined"
                                                    color="primary"
                                                    startIcon={<DeleteIcon/>}
                                                >
                                                    {t("analyzer.DeleteRunButton")}
                                                </Button>
                                            </Grid>

                                        </Grid>
                                    </Grid>
                                )
                            }

                            {
                                runData && selectedRun && (() => {
                                    switch (runData.params.experimentType) {
                                        case "Classification":
                                            return <AnalyzeClassification runData={runData}/>;
                                        case "Forecast":
                                            return <AnalyzeForecast runData={runData}/>;
                                        default:
                                            return <AnalyzeRegression runData={runData}/>;
                                    }
                                })()
                            }

                            {((runData.params.stepName.startsWith("AFS") || runData.params.stepName === "FeatureSelection") && runData.afsDetails) && (
                                // if the step is FeatureSelection, show the AFS analysis
                                // the if is to allow legacy runs to display correctly TODO@legacy now it is only AFS
                                <AnalyzeAFS runData={runData}/>
                            )}
                        </>
                    ) : (
                        // if user lands on this page, without having all required steps fulfilled, show hint
                        <Grid item xs={12}>
                            {" "}
                            <Typography variant="body">
                                {t("analyzer.NoExperiments")}
                                <Link to="/train">{t("analyzer.NoExperiments_LinkText")}</Link>
                                {t("analyzer.NoExperiments_AfterLink")}
                            </Typography>
                        </Grid>
                    )
                }
            </Grid>
        </Grid>
    )
        ;
}
